import type {StepPayload} from '@blesk-frontend/api';
import {getBoundingRect} from './getBoundingRect';

/** Editor frame aspect ratio (matches `aspect-video`). */
const FRAME_ASPECT = 16 / 9;
/** Padding around highlight bounding box before expanding to 16:9. */
const AUTO_FRAME_PADDING = 0.15;
/**
 * Minimum auto-crop size as a fraction of the largest 16:9 frame that fits the image.
 * Prevents extreme zoom on a single small highlight (≈ max 1.67× relative to full frame).
 */
const AUTO_FRAME_MIN_SIZE = 0.6;

export type CropRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type StepViewport = CropRect;

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(value, max));

const clampRect = (rect: CropRect, imageWidth: number, imageHeight: number): CropRect => {
  const width = clamp(rect.width, 0, imageWidth);
  const height = clamp(rect.height, 0, imageHeight);
  return {
    x: clamp(rect.x, 0, imageWidth - width),
    y: clamp(rect.y, 0, imageHeight - height),
    width,
    height,
  };
};

/** Largest 16:9 rectangle centered in the image. */
const centerAspectRect = (imageWidth: number, imageHeight: number): CropRect => {
  const imageIsWider = imageWidth / imageHeight > FRAME_ASPECT;
  if (imageIsWider) {
    const width = imageHeight * FRAME_ASPECT;
    return {x: (imageWidth - width) / 2, y: 0, width, height: imageHeight};
  }
  const height = imageWidth / FRAME_ASPECT;
  return {x: 0, y: (imageHeight - height) / 2, width: imageWidth, height};
};

/** Expand a bounding box to 16:9 with padding, clamped to image bounds. */
const autoFrameRect = (bounding: CropRect, imageWidth: number, imageHeight: number): CropRect => {
  const pad = Math.min(bounding.width, bounding.height) * AUTO_FRAME_PADDING;
  const paddedWidth = bounding.width + pad * 2;
  const paddedHeight = bounding.height + pad * 2;
  const maxFrame = centerAspectRect(imageWidth, imageHeight);
  const width = Math.min(
    maxFrame.width,
    Math.max(paddedWidth, paddedHeight * FRAME_ASPECT, maxFrame.width * AUTO_FRAME_MIN_SIZE),
  );
  const height = width / FRAME_ASPECT;

  return clampRect(
    {
      x: bounding.x + bounding.width / 2 - width / 2,
      y: bounding.y + bounding.height / 2 - height / 2,
      width,
      height,
    },
    imageWidth,
    imageHeight,
  );
};

export const hasUserViewport = (editor: StepPayload['editor']): boolean => {
  if (editor.viewport) return true;
  return (
    editor.zoom != null && Number.isFinite(editor.zoom) && editor.zoom > 0 && editor.pan != null
  );
};

/**
 * Visible region of the image inside the editor frame, in natural pixels.
 * Uses transformed image and frame getBoundingClientRect (includes CSS transform).
 */
export const measureVisibleCropRect = (
  frameEl: HTMLElement,
  imageEl: HTMLImageElement,
): CropRect | null => {
  const {naturalWidth, naturalHeight} = imageEl;
  if (!naturalWidth || !naturalHeight) return null;

  const frame = frameEl.getBoundingClientRect();
  const image = imageEl.getBoundingClientRect();
  if (!image.width || !image.height) return null;

  const scaleX = naturalWidth / image.width;
  const scaleY = naturalHeight / image.height;

  return clampRect(
    {
      x: (frame.left - image.left) * scaleX,
      y: (frame.top - image.top) * scaleY,
      width: frame.width * scaleX,
      height: frame.height * scaleY,
    },
    naturalWidth,
    naturalHeight,
  );
};

/**
 * Resolve crop rectangle in natural image pixels.
 * Manual path when user viewport is set; otherwise auto-frame 16:9.
 */
export const resolveCropRect = (
  step: Pick<StepPayload, 'editor'>,
  imageWidth: number,
  imageHeight: number,
): CropRect => {
  const {editor} = step;

  if (editor.viewport) {
    return clampRect(editor.viewport, imageWidth, imageHeight);
  }

  const {zoom, pan} = editor;
  if (zoom != null && Number.isFinite(zoom) && zoom > 0 && pan) {
    const {frameWidth, frameHeight, x: panX, y: panY} = pan;
    if (frameWidth != null && frameWidth > 0 && frameHeight != null && frameHeight > 0) {
      const width = frameWidth / zoom;
      const height = frameHeight / zoom;
      // Content at scale 1 is natural size; library translate is pan after scale.
      const contentOffsetX = (frameWidth - imageWidth * zoom) / 2 + panX;
      const contentOffsetY = (frameHeight - imageHeight * zoom) / 2 + panY;
      return clampRect(
        {x: -contentOffsetX / zoom, y: -contentOffsetY / zoom, width, height},
        imageWidth,
        imageHeight,
      );
    }
  }

  return resolveAutoCropRect(editor.rects, imageWidth, imageHeight);
};

/** Canonical 16:9 auto-frame in natural image pixels (no user viewport). */
export const resolveAutoCropRect = (
  rects: StepPayload['editor']['rects'],
  imageWidth: number,
  imageHeight: number,
): CropRect => {
  const annotationRects = rects.filter(({type}) => type === 'highlight' || type === 'circle');
  const bounding = getBoundingRect(annotationRects);
  if (!bounding) {
    return centerAspectRect(imageWidth, imageHeight);
  }
  return autoFrameRect(bounding, imageWidth, imageHeight);
};

/** Convert natural-pixel rect to absolute crop params for the crop API / imgproxy. */
export const cropRectToApiParams = (
  rect: CropRect,
  imageWidth: number,
  imageHeight: number,
): {
  cropWidth: number;
  cropHeight: number;
  cropOffset: {x: number; y: number};
} => ({
  // imgproxy: >= 1 = pixels, < 1 = fraction. Never send relative `1` (becomes 1px).
  cropWidth: Math.max(1, Math.round(Math.min(rect.width, imageWidth))),
  cropHeight: Math.max(1, Math.round(Math.min(rect.height, imageHeight))),
  cropOffset: {
    x: Math.round(rect.x + rect.width / 2 - imageWidth / 2),
    y: Math.round(rect.y + rect.height / 2 - imageHeight / 2),
  },
});
